import classNames from "classnames";
import { PropTypes } from "prop-types";
import { Fragment, useEffect, useRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import styles from "./ImageEditor.module.css";

/**
 * <ImageEditor
 * image={img}
 * isBusy={bool}
 * updateImage={()=>update()}
 * closeEditor={()=>close()}
 * style={{height:int,width:int}} // access  to the avatar editor. height and width dictate styling and not canvas pixel density
 * className ={styles.container}
 * />
 */

export function ImageEditor({
  image,
  isBusy = false,
  updateImage,
  closeEditor,
  style,
  className,
}) {
  const defaultState = {
    allowZoomOut: false,
    scale: 1,
    rotate: 0,
    borderRadius: 50,
    preview: null,
    width: 141,
    height: 141,
    maxZoom: "4",
  };

  const [state, setState] = useState({ ...defaultState, ...style });

  const [loading, setLoading] = useState(true);

  const editor = useRef(null);
  const editorContainer = useRef(null);

  useEffect(() => {
    // This useEffect runs before and after the next useEffect, providing a cleanup opportunity
    if (editorContainer.current) {
      editorContainer.current.style.width = style.width + "px";
      editorContainer.current.style.height = style.height + "px";

      let editorContainerWidth = editorContainer.current.style.width;
      let editorContainerHeight = editorContainer.current.style.height;

      if (
        editorContainerWidth === style.width + "px" &&
        editorContainerHeight === style.height + "px"
      ) {
        if (editor.current) {
          editor.current.canvas.style.width = "100%";
          editor.current.canvas.style.height = "100%";

          if (state.modified) {
            setLoading(false);
            editor.current.canvas.style.visibility = "visible";
          }
        }
      }
    }
  }, [state.width, state.height, state.modified]);

  var convertImgToDataURLviaCanvas = function (url, callback) {
    var img = new Image();

    img.crossOrigin = "Anonymous";

    img.onload = function () {
      var canvas = document.createElement("CANVAS");
      var ctx = canvas.getContext("2d");
      var dataURL;
      canvas.height = this.height;
      canvas.width = this.width;
      ctx.drawImage(this, 0, 0);
      dataURL = canvas.toDataURL();
      callback(dataURL);
      canvas = null;
    };

    img.src = url;
  };
  const [_image, setImage] = useState(null);
  useEffect(() => {
    if (image) {
      convertImgToDataURLviaCanvas(image, (e) => setImage(e));
    }
  }, [image]);

  useEffect(() => {
    image && setState({ ...state, image: image });
  }, [image]);

  useEffect(() => {
    if (image) {
      // This determination of which dimension is larger is essential to utilize the borderRadius of react-avatar-modal
      let largerDimension = image.width - image.height;
      if (largerDimension < 1) {
        largerDimension = image.height - image.width;
      }
      if (!largerDimension) {
        largerDimension = image.width;
      }

      // we 'square' the canvas dimensions to allow the border radius drawn on the canvas to be circular
      // the addition of 'modified' triggers the previous useEffect for cleanup
      setState({
        ...state,
        image: "",
        width: largerDimension,
        height: largerDimension,
        borderRadius: largerDimension / (100 / state.borderRadius),
        modified: true,
      });
    }
  }, []);
  console.log(state.image);

  function handleScale(e) {
    const scale = parseFloat(e.target.value);
    setState({ ...state, scale });
  }

  function handlePositionChange(position) {
    setState({ ...state, position });
  }

  function handleRotation() {
    let degrees = 0;
    if (state.rotate === 0) {
      degrees = 90;
    } else if (state.rotate === 90) {
      degrees = 180;
    } else if (state.rotate === 180) {
      degrees = 270;
    }

    setState({ ...state, rotate: degrees });
  }

  function onClickSave() {
    if (editor) {
      console.log(editor);
      const image = editor.current
        .getImageScaledToCanvas()
        .toDataURL("image/jpeg", 1.0);

      updateImage(image);
      closeEditor();
    }
  }

  if (!image) {
    return <Fragment />;
  }

  return (
    <Fragment>
      <div className={classNames(styles.imageEditor, className)}>
        <div ref={editorContainer} className={styles.editorContainer}>
          <AvatarEditor
            ref={editor}
            scale={parseFloat(state.scale)}
            width={148}
            height={148}
            position={state.position}
            onPositionChange={handlePositionChange}
            rotate={parseFloat(state.rotate)}
            borderRadius={100}
            border={0}
            image={_image}
          />
        </div>

        <div className={styles.imageEditorControls}>
          <div>
            <label htmlFor="slider">Zoom</label>
            <input
              className={styles.slider}
              id="slider"
              name="scale"
              type="range"
              onChange={handleScale}
              min={state.allowZoomOut ? ".1" : "1"}
              max={state.maxZoom}
              step="0.01"
              defaultValue="1"
            />
          </div>
          <hr />
          <div className={styles.buttonContainer}>
            <div className={styles.row1}>
              <button
                type="button"
                className={styles.btn}
                onClick={handleRotation}
                disabled={isBusy}
              >
                Rotate
              </button>
            </div>
            <div className={styles.row2}>
              <button
                type="button"
                className={classNames(styles.btn, styles.saveBtn)}
                onClick={closeEditor}
                disabled={isBusy}
              >
                Cancel
              </button>

              <button
                type="button"
                className={classNames(styles.btn, styles.saveBtn)}
                onClick={onClickSave}
                disabled={isBusy}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

ImageEditor.propTypes = {
  /**
   * image : the image to modify
   */
  image: PropTypes.string,
  /**
   * isBusy : the bool to determine when to disable controls during function execution
   */
  isBusy: PropTypes.bool,
  /**
   * updateImage : function to execute on the resulting canvas clipped image
   */
  updateImage: PropTypes.func,
  /**
   * closeEditor : function to execute after updateImage
   */
  closeEditor: PropTypes.func,
  /**
   * style : an object to pass to the react-avatar-editor instance https://www.npmjs.com/package/react-avatar-editor
   */
  style: PropTypes.object,
  /**
   * className : access to the most external dom element's styles
   */
  className: PropTypes.object,
};
